// Two-device "I Go You Go" (IGYG) partner sessions over Supabase Broadcast +
// Presence. Ephemeral and host-anchored: the host owns the authoritative snapshot
// (training + shared start timestamp + combined reps) and (re)broadcasts it; the
// guest hydrates from it. Like ./realtime.ts, broadcast + presence need no Supabase
// dashboard config, no table, and no RLS. There is no backend involved.
//
// Rep model is conflict-free: each partner only ever mutates their OWN sub-count
// (reps[exerciseId][selfUserId]) via taps and broadcasts a `rep` delta; the peer
// applies deltas for the sender only. Combined = sum of both. The host's periodic
// `state` snapshot is used purely for initial hydration / reconnect resync.

import { supabase } from './supabase';
import { API_BASE } from './apiBase';
import { authFetch } from './authFetch';

const PARTNER_INVITE_EVENT = 'partner-invite';
// Dedicated invite channel (NOT the shared `user:<id>` channel the Header uses),
// so an invite subscription never collides with that one.
const inviteChannelName = (userId: string) => `partner-invite:${userId}`;
const partnerChannelName = (sessionId: string) => `partner:${sessionId}`;

export const PARTNER_INVITE = PARTNER_INVITE_EVENT;

// Window events linking the Header bell and PartnerInvitePrompt, which are
// siblings with no shared context. `SHOW` carries a stored invite the user
// tapped in the bell so the prompt re-opens it; `REFRESH` tells the bell to
// re-read /pending after the prompt resolved one.
export const PARTNER_INVITE_SHOW_EVENT = 'partner-invite:show';
export const PARTNER_INVITE_REFRESH_EVENT = 'partner-invite:refresh';

export type PartnerInvitePayload = {
  sessionId: string;
  hostUserId: string;
  hostName: string;
  trainingTitle: string;
  // The full guest-runnable training DTO, so the guest can launch immediately
  // without needing DB access to the host's training. (Well under broadcast size.)
  training: any;
};

export type PartnerSelf = { userId: string; name: string };

/** exerciseId -> userId -> reps done by that user */
export type PartnerReps = Record<string, Record<string, number>>;

export type PartnerStatePayload = {
  training: any; // full PlannedTrainingDTO so the guest can run the same block
  startedAtServerMs: number | null;
  reps: PartnerReps;
  finished?: boolean;
};

export type PartnerRepPayload = { userId: string; exerciseId: string; delta: number };

export type PartnerHandlers = {
  /** Realtime is unusable (blocked socket); the caller should degrade to solo. */
  onUnavailable?: (error: Error) => void;
  onReady?: () => void;
  onHello?: (payload: { userId: string; name: string }) => void;
  onState?: (payload: PartnerStatePayload) => void;
  onRep?: (payload: PartnerRepPayload) => void;
  onFinish?: () => void;
  onPresence?: (members: Array<{ userId: string; name: string }>) => void;
};

export type PartnerChannelHandle = {
  sendHello: () => void;
  sendState: (payload: PartnerStatePayload) => void;
  sendRep: (payload: PartnerRepPayload) => void;
  sendFinish: () => void;
  leave: () => void;
};

const NOOP_HANDLE: PartnerChannelHandle = {
  sendHello() {}, sendState() {}, sendRep() {}, sendFinish() {}, leave() {},
};

/**
 * Host side: invite a friend.
 *
 * Two deliveries, on purpose. The broadcast is instant but ephemeral — it only
 * lands if the guest already has the app open, and leaves no trace otherwise.
 * The API call stores the invite so it is still waiting the next time they open
 * the app. Persisting first means a failed broadcast doesn't lose the invite.
 */
export async function sendPartnerInvite(
  friendUserId: string,
  payload: PartnerInvitePayload,
): Promise<void> {
  if (!friendUserId) return;

  try {
    await authFetch(`${API_BASE}/api/partner-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: payload.sessionId,
        guestUserId: friendUserId,
        hostName: payload.hostName,
        trainingTitle: payload.trainingTitle,
        training: payload.training,
      }),
    });
  } catch (e) {
    // Best effort: a live partner still gets the broadcast below.
    console.warn('[partner] persisting invite failed', e);
  }

  try {
    const channel = supabase.channel(inviteChannelName(friendUserId));
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => { if (!settled) { settled = true; resolve(); } };
      channel.subscribe((status) => { if (status === 'SUBSCRIBED') finish(); });
      setTimeout(finish, 1500); // don't hang if realtime is unavailable
    });
    await channel.send({ type: 'broadcast', event: PARTNER_INVITE_EVENT, payload });
    setTimeout(() => { void supabase.removeChannel(channel); }, 500);
  } catch (e) {
    console.warn('[partner] sendPartnerInvite failed', e);
  }
}

/** Guest side: listen for incoming partner invites on your dedicated channel. */
export function subscribePartnerInvites(
  userId: string,
  onInvite: (payload: PartnerInvitePayload) => void,
): () => void {
  if (!userId) return () => {};
  let channel: ReturnType<typeof supabase.channel> | null = null;
  try {
    channel = supabase
      .channel(inviteChannelName(userId))
      .on('broadcast', { event: PARTNER_INVITE_EVENT }, (msg: any) => {
        const p = msg?.payload;
        if (p?.sessionId && p?.training) onInvite(p as PartnerInvitePayload);
      })
      .subscribe();
  } catch (e) {
    console.warn('[partner] subscribePartnerInvites failed', e);
  }
  return () => { if (channel) void supabase.removeChannel(channel); };
}

/**
 * Join (or host) a partner session channel. Wires broadcast + presence handlers
 * and returns senders. Call the returned senders after `onReady` fires.
 */
export function joinPartnerChannel(
  sessionId: string,
  self: PartnerSelf,
  handlers: PartnerHandlers,
): PartnerChannelHandle {
  if (!sessionId || !self?.userId) return NOOP_HANDLE;
  let channel: ReturnType<typeof supabase.channel>;
  try {
    channel = supabase.channel(partnerChannelName(sessionId), {
      config: { presence: { key: self.userId } },
    });

    // `subscribe()` opens the socket synchronously, so it throws right here when
    // the browser refuses one — Safari's Lockdown Mode and some content blockers
    // raise "The operation is insecure." Kept inside the try because an IGYG
    // block is perfectly usable solo: degrade to a no-op handle rather than
    // letting this take down the running workout.
    channel
      .on('broadcast', { event: 'hello' }, ({ payload }: any) => handlers.onHello?.(payload))
      .on('broadcast', { event: 'state' }, ({ payload }: any) => handlers.onState?.(payload))
      .on('broadcast', { event: 'rep' }, ({ payload }: any) => handlers.onRep?.(payload))
      .on('broadcast', { event: 'finish' }, () => handlers.onFinish?.())
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState() as Record<string, Array<{ userId?: string; name?: string }>>;
        const members: Array<{ userId: string; name: string }> = [];
        for (const key of Object.keys(state)) {
          const meta = state[key]?.[0];
          members.push({ userId: meta?.userId ?? key, name: meta?.name ?? '' });
        }
        handlers.onPresence?.(members);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void channel.track({ userId: self.userId, name: self.name });
          handlers.onReady?.();
        }
      });
  } catch (e) {
    console.warn('[partner] joinPartnerChannel failed', e);
    handlers.onUnavailable?.(e instanceof Error ? e : new Error(String(e)));
    return NOOP_HANDLE;
  }

  const send = (event: string, payload: any) => {
    try { void channel.send({ type: 'broadcast', event, payload }); }
    catch { /* best effort */ }
  };

  return {
    sendHello: () => send('hello', { userId: self.userId, name: self.name }),
    sendState: (payload) => send('state', payload),
    sendRep: (payload) => send('rep', payload),
    sendFinish: () => send('finish', {}),
    leave: () => { try { void supabase.removeChannel(channel); } catch { /* ignore */ } },
  };
}
